import { v4 as uuidv4 } from 'uuid';
import { TUploadDocumentDataRequestCamel } from '@/types';

/**
 * Converts a base64 data URL to a File object.
 * @param dataUrl - The data URL (e.g., "data:image/png;base64,...").
 * @param createdAt - Optional creation date string for the file.
 * @param name - Optional file name, defaults to 'document'.
 * @returns A File object representing the data URL content.
 */
export const convertDataURLToFile = (
  dataUrl: string,
  createdAt?: string,
  name: string = 'document',
): File => {
  const byteString = atob(dataUrl.split(',')[1]);
  const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];

  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], name, {
    type: mimeString,
    lastModified: createdAt ? new Date(createdAt).getTime() : Date.now(),
  });
};

/**
 * Converts a File object to TUploadDocumentDataCamel format with base64 encoding.
 * @param file - The File object to convert.
 * @param userInstitutionRoleId - The user institution role ID.
 * @returns A promise that resolves to TUploadDocumentDataCamel object.
 */
export const convertFileToUploadData = (
  file: File,
  userInstitutionRoleId: string = '',
): Promise<TUploadDocumentDataRequestCamel> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64 = reader.result?.toString() ?? '';

      const uploadData: TUploadDocumentDataRequestCamel = {
        id: uuidv4(),
        uri: base64,
        name: file.name,
        createdAt: new Date().toISOString(),
        size: file.size,
        userInstitutionRoleId,
      };

      resolve(uploadData);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};
